/* Cactus Warehouse v2 motion layer.
   Reveal on scroll, lazy video, process step sync, marquee fill, word split.
   Vanilla, no dependencies. Safe to load once; re-inits on theme editor section loads. */
(function () {
  if (window.cwV2) return;

  var doc = document;
  var root = doc.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;
  var revealIO = null;
  var videoIO = null;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || root.clientHeight);
  }

  /* Word split: wraps each word of [data-cw-split] in a span with --i for staggered CSS. */
  function split(scope) {
    each(scope.querySelectorAll('[data-cw-split]:not([data-cw-split-done])'), function (el) {
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;
      el.setAttribute('data-cw-split-done', '');
      el.setAttribute('aria-label', text);
      el.textContent = '';
      text.split(' ').forEach(function (w, i, all) {
        var s = doc.createElement('span');
        s.className = 'cw-word';
        s.setAttribute('aria-hidden', 'true');
        s.style.setProperty('--i', i);
        s.textContent = w;
        el.appendChild(s);
        if (i < all.length - 1) el.appendChild(doc.createTextNode(' '));
      });
    });
  }

  /* Reveal: elements already on screen are marked in before html.cw-ready turns hiding on,
     so nothing above the fold ever flashes. Without JS everything simply shows. */
  function reveal(scope) {
    each(scope.querySelectorAll('.cw-reveal-stagger'), function (p) {
      each(p.children, function (c, i) { c.style.setProperty('--i', i); });
    });
    var els = scope.querySelectorAll('.cw-reveal:not(.is-in)');
    if (reduce || !hasIO) {
      each(els, function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            revealIO.unobserve(en.target);
          }
        });
      }, { threshold: 0.15 });
    }
    each(els, function (el) {
      if (inView(el)) el.classList.add('is-in');
      else revealIO.observe(el);
    });
  }

  /* Lazy video: <video data-cw-src> gets its src only near the viewport, pauses off screen.
     Videos hidden with display:none never intersect, so they never download. */
  function loadVideo(v) {
    if (v.getAttribute('data-cw-loaded')) return;
    v.setAttribute('data-cw-loaded', '1');
    v.muted = true;
    if (reduce) v.removeAttribute('autoplay');
    v.src = v.getAttribute('data-cw-src');
    v.load();
  }
  function play(v) {
    if (reduce) return;
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }
  function videos(scope) {
    var vids = scope.querySelectorAll('video[data-cw-src]');
    if (!hasIO) {
      each(vids, function (v) { if (v.offsetParent !== null) { loadVideo(v); play(v); } });
      return;
    }
    if (!videoIO) {
      videoIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) { loadVideo(v); play(v); }
          else if (v.getAttribute('data-cw-loaded')) { v.pause(); }
        });
      }, { rootMargin: '200px 0px' });
    }
    each(vids, function (v) { videoIO.observe(v); });
  }

  /* Process: the step whose video card is closest to the viewport center is active.
     --cw-progress (0..1) drives the active step's progress bar. */
  var processTick = false;
  function processUpdate() {
    processTick = false;
    var mid = (window.innerHeight || root.clientHeight) / 2;
    each(doc.querySelectorAll('[data-cw-process]'), function (sec) {
      var cards = sec.querySelectorAll('[data-cw-step-media]');
      var steps = sec.querySelectorAll('[data-cw-step]');
      var best = 0, bestD = Infinity, bestR = null;
      each(cards, function (c, i) {
        var r = c.getBoundingClientRect();
        if (!r.height) return;
        var d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = i; bestR = r; }
      });
      if (!bestR) return;
      var prog = Math.min(1, Math.max(0, (mid - bestR.top) / bestR.height));
      each(steps, function (s, i) {
        var on = i === best;
        s.classList.toggle('is-active', on);
        s.style.setProperty('--cw-progress', on ? prog.toFixed(3) : (i < best ? 1 : 0));
      });
      each(cards, function (c, i) { c.classList.toggle('is-active', i === best); });
    });
  }
  function onScroll() {
    if (processTick) return;
    processTick = true;
    window.requestAnimationFrame(processUpdate);
  }
  function process(scope) {
    each(scope.querySelectorAll('[data-cw-process]:not([data-cw-bound])'), function (sec) {
      sec.setAttribute('data-cw-bound', '');
      sec.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-cw-step-go]') : null;
        if (!btn) return;
        var card = sec.querySelector('[data-cw-step-media="' + btn.getAttribute('data-cw-step-go') + '"]');
        if (card) card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    });
    processUpdate();
  }

  /* Marquee: fill one group until it is wider than the viewport, then clone it once
     so translateX(-50%) loops seamlessly. */
  function marquee(scope) {
    each(scope.querySelectorAll('[data-cw-marquee]:not(.is-ready)'), function (m) {
      var track = m.querySelector('.cw-marquee__track');
      var group = track && track.querySelector('.cw-marquee__group');
      if (!group || !group.children.length) return;
      var originals = Array.prototype.slice.call(group.children);
      var guard = 0;
      var need = (window.innerWidth || root.clientWidth) + 100;
      while (group.scrollWidth < need && guard < 8) {
        originals.forEach(function (n) {
          var c = n.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          each(c.querySelectorAll('a'), function (a) { a.setAttribute('tabindex', '-1'); });
          group.appendChild(c);
        });
        guard++;
      }
      var copy = group.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      each(copy.querySelectorAll('a'), function (a) { a.setAttribute('tabindex', '-1'); });
      track.appendChild(copy);
      m.style.setProperty('--cw-marquee-fill', String(guard + 1));
      m.classList.add('is-ready');
    });
  }

  function init(scope) {
    scope = scope || doc;
    split(scope);
    marquee(scope);
    reveal(scope);
    videos(scope);
    process(scope);
  }

  window.cwV2 = { init: init };

  // Oct 8 2026 fix A: mirror the cart drawer's open state onto <body> (fallback for browsers without :has)
  function drawerWatch() {
    var d = doc.querySelector('cart-drawer');
    if (!d || !window.MutationObserver) return;
    var sync = function () { doc.body.classList.toggle('cw-drawer-open', d.hasAttribute('open')); };
    new MutationObserver(sync).observe(d, { attributes: true, attributeFilter: ['open'] });
    sync();
  }

  function start() {
    init(doc);
    drawerWatch();
    root.classList.add('cw-ready');
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();

  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
